import type { ReactElement, SVGProps } from 'react';

import AlertFill from '@/assets/icons/alert_fill.svg?component';
import AlertOctagonFill from '@/assets/icons/alert_octagon_fill.svg?component';
import CheckCircleFill from '@/assets/icons/check_circle_fill.svg?component';
import FirstAidKitFill from '@/assets/icons/first_aid_kit_fill.svg?component';
import Home3Fill from '@/assets/icons/home_3_fill.svg?component';
import Louse from '@/assets/icons/louse.svg?component';
import MoveFill from '@/assets/icons/move_fill.svg?component';
import BioscopeLux from '@/assets/icons/operational-dashboard/bioscope-lux.svg?component';
import SafeShield2Fill from '@/assets/icons/safe_shield_2_fill.svg?component';
import VideoCamera2Fill from '@/assets/icons/video_camera_2_fill.svg?component';
import Winch from '@/assets/icons/winch.svg?component';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';

import type { ToastKind } from './toastStore';

const ICONS = {
    lice: Louse,
    health: FirstAidKitFill,
    fishspeed: Home3Fill,
    optoscale: Home3Fill,
    winch: MoveFill,
    Laksvel: SafeShield2Fill,
    video: VideoCamera2Fill,
    new_winch: Winch,
    success: CheckCircleFill,
    // Bioscope and winch failures name these; missing from the map, they drew a blank slot.
    error: AlertOctagonFill,
    lux: BioscopeLux,
};

// Warning, error and success match the border fills of those toasts; an info icon follows
// the text colour, so it stays legible in both themes.
const ICON_COLORS: Record<ToastKind, string> = {
    warning: 'var(--color-caution-fill)',
    error: 'var(--color-danger-fill)',
    success: 'var(--color-success-fill)',
    info: 'var(--color-text)',
};

function isToastIconKey(key: string): key is keyof typeof ICONS {
    return key in ICONS;
}

/** Draws nothing in the icon's 24px slot, for keys such as `noicon` that have no glyph. */
function EmptyGlyph(props: SVGProps<SVGSVGElement>) {
    const { width, height, className, style } = props;
    return (
        <svg width={width} height={height} className={className} style={style} aria-hidden="true" />
    );
}

/** The glyph for a `ToastInfo` icon key, coloured by the toast's status. */
export function renderToastIcon(icon: string, status: ToastKind): ReactElement {
    const glyph = isToastIconKey(icon) ? ICONS[icon] : EmptyGlyph;
    return <SvgIcon icon={glyph} color={ICON_COLORS[status]} />;
}

/** The glyph a feedback toast shows for its kind: a warning triangle or an error sign. */
export function renderFeedbackIcon(kind: 'warning' | 'error'): ReactElement {
    return (
        <SvgIcon
            icon={kind === 'warning' ? AlertFill : AlertOctagonFill}
            size="custom"
            customSize={20}
            color={ICON_COLORS[kind]}
        />
    );
}
